import { useState } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import RepairItemPickerDialog from '#/modules/crmMgmt/aftersales/signReceipt/components/RepairItemPickerDialog';

function displayDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

/**
 * 簽收明細編輯：只維護 repairItemIds；顯示列來自 selectedItems。
 * 禁 SN／report 輸入欄。
 */
const SignReceiptItemsEditor = ({
  control,
  setValue,
  selectedItems,
  onSelectedItemsChange,
  errors,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const idsError = errors?.repairItemIds;

  function handleConfirm(items) {
    onSelectedItemsChange(items);
    setValue(
      'repairItemIds',
      items.map((item) => item.id).filter(Boolean),
      { shouldValidate: true, shouldDirty: true },
    );
  }

  function handleRemove(id) {
    const next = selectedItems.filter((item) => item.id !== id);
    onSelectedItemsChange(next);
    setValue(
      'repairItemIds',
      next.map((item) => item.id).filter(Boolean),
      { shouldValidate: true, shouldDirty: true },
    );
  }

  return (
    <section className="@container/form-section pb-6">
      <div className="form-section__header">
        <h2 className="form-block__title mr-2">簽收品項</h2>
        <Button type="button" size="sm" onClick={() => setPickerOpen(true)}>
          <Plus className="size-3.5" aria-hidden />
          選取維修品項
        </Button>
      </div>

      <Controller
        name="repairItemIds"
        control={control}
        render={() => null}
      />

      {idsError?.message ? (
        <p className="mt-1 text-sm text-destructive">{idsError.message}</p>
      ) : null}

      {selectedItems.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">尚未選取維修品項</p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full min-w-[40rem] text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="px-3 py-2 font-medium">序號</th>
                <th className="px-3 py-2 font-medium">產品</th>
                <th className="px-3 py-2 font-medium">問題描述</th>
                <th className="px-3 py-2 font-medium">配件</th>
                <th className="w-16 px-3 py-2 font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {selectedItems.map((item) => (
                <tr key={item.id}>
                  <td className="px-3 py-2 font-medium text-gray-900">
                    {displayDash(item.serialNo)}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {displayDash(item.productCode)}
                    {' · '}
                    {displayDash(item.itemName)}
                  </td>
                  <td className="max-w-xs truncate px-3 py-2 text-gray-600">
                    {displayDash(item.problemDescription)}
                  </td>
                  <td className="max-w-[8rem] px-3 py-2 text-gray-600">
                    <TruncatedTextWithTooltip
                      text={displayDash(item.accessories)}
                      contentClassName="whitespace-pre-line"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className={cn('size-8 text-destructive hover:text-destructive')}
                      aria-label="移除品項"
                      onClick={() => handleRemove(item.id)}
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RepairItemPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedItems={selectedItems}
        onConfirm={handleConfirm}
      />
    </section>
  );
};

SignReceiptItemsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelectedItemsChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default SignReceiptItemsEditor;
